// src/tabs/RouteTab.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTabs } from "./TabContext";
import styles from "./Tab.module.css";

// ✅ TabContext와 동일하게 유지 (메인 탭 보호)
const MAIN_PATH = "/dashboard";

export default function RouteTabs() {
  const { state, dispatch } = useTabs();
  const navigate = useNavigate();

  // ✅ 드래그 앤 드롭 순서 변경용
  const [draggedPath, setDraggedPath] = useState(null);
  // 드롭 시 삽입될 위치(0=맨 앞, tabs.length=맨 끝) — 탭 사이 경계에 표시되는 인디케이터 위치와 동일
  const [dropIndex, setDropIndex] = useState(null);

  const onClickTab = (path) => {
    dispatch({ type: "ACTIVATE", payload: path });
    navigate(path, { replace: path === state.activePath });
  };

  const onClose = (e, path) => {
    e.stopPropagation();
    dispatch({ type: "CLOSE", payload: path });
  };

  const closeOthers = () => dispatch({ type: "CLOSE_OTHERS", payload: state.activePath });
  const closeAll = () => dispatch({ type: "CLOSE_ALL" });

  // ✅ 메인 탭은 드래그 대상에서 제외
  const onDragStart = (e, path) => {
    if (path === MAIN_PATH) { e.preventDefault(); return; }
    setDraggedPath(path);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", path); // 일부 브라우저는 setData 없이는 드래그가 시작되지 않음
  };

  // ✅ 탭 하나 위에서 — 마우스 X가 왼쪽 절반이면 "이 탭 앞", 오른쪽 절반이면 "이 탭 뒤"
  const onTabDragOver = (e, hoverIndex) => {
    if (!draggedPath || state.tabs[hoverIndex]?.path === draggedPath) return;
    e.preventDefault();
    e.stopPropagation(); // 컨테이너의 "빈 영역 = 맨 끝" 폴백이 겹쳐 실행되지 않도록
    e.dataTransfer.dropEffect = "move";

    const rect = e.currentTarget.getBoundingClientRect();
    const isLeftHalf = e.clientX < rect.left + rect.width / 2;
    const next = isLeftHalf ? hoverIndex : hoverIndex + 1;
    if (dropIndex !== next) setDropIndex(next);
  };

  // ✅ 탭바의 탭 없는 빈 영역(맨 오른쪽 여백 등) — 리스트 맨 끝으로 처리
  const onContainerDragOver = (e) => {
    if (!draggedPath) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropIndex !== state.tabs.length) setDropIndex(state.tabs.length);
  };

  const commitDrop = () => {
    if (draggedPath != null && dropIndex != null) {
      dispatch({ type: "REORDER", payload: { from: draggedPath, toIndex: dropIndex } });
    }
    setDraggedPath(null);
    setDropIndex(null);
  };

  const onTabDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    commitDrop();
  };

  const onContainerDrop = (e) => {
    e.preventDefault();
    commitDrop();
  };

  const onDragEnd = () => {
    // ✅ 드롭 없이 취소(Escape 등)되는 경우까지 포함해 항상 상태 정리
    setDraggedPath(null);
    setDropIndex(null);
  };

  if (!state.tabs?.length) return null;

  return (
    <div className={styles.wrap} onDragOver={onContainerDragOver} onDrop={onContainerDrop}>
      <div className={styles.tabs}>
        {state.tabs.map((t, i) => {
          // ✅ 라벨/아이콘 기본값 보강
          const lastSeg = t.path.split("/").filter(Boolean).pop();
          const label = t.title || (t.path === MAIN_PATH ? "메인" : lastSeg || "페이지");
          const icon = t.icon || (t.path === MAIN_PATH ? "🏠" : "📄");
          const isMain = t.path === MAIN_PATH;

          return (
            <React.Fragment key={t.path}>
              {dropIndex === i && <div className={styles.dropIndicator} />}
              <div
                className={[
                  styles.tab,
                  state.activePath === t.path ? styles.active : "",
                  draggedPath === t.path ? styles.dragging : "",
                ].filter(Boolean).join(" ")}
                onClick={() => onClickTab(t.path)}
                title={label}
                role="tab"
                aria-selected={state.activePath === t.path}
                draggable={!isMain}
                onDragStart={(e) => onDragStart(e, t.path)}
                onDragOver={(e) => onTabDragOver(e, i)}
                onDrop={onTabDrop}
                onDragEnd={onDragEnd}
              >
                <span className={styles.icon}>{icon}</span>
                <span className={styles.title}>{label}</span>

                {/* ✅ 메인 탭은 닫기 금지 */}
                {!isMain && (
                  <button
                    type="button"
                    className={styles.close}
                    onClick={(e) => onClose(e, t.path)}
                    aria-label={`${label} 탭 닫기`}
                  >
                    ×
                  </button>
                )}
              </div>
            </React.Fragment>
          );
        })}
        {dropIndex === state.tabs.length && <div className={styles.dropIndicator} />}
      </div>

      <div className={styles.tools}>
        <button type="button" onClick={closeOthers}>다른 탭 닫기</button>
        <button type="button" onClick={closeAll}>모두 닫기</button>
      </div>
    </div>
  );
}
